import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { TFunction } from "react-i18next";
import { updateDomainFields } from "../../utils";
import { LocationsLayerInfo } from "../shared/shared.types";
import { updateOpenNow } from "./OpenNowHelper";

const _getServices = (
  loc: Graphic,
  format: "byvalue" | "notnull",
  fieldLabels: any,
  fields?: string[]
) => {
  const services: string[] = [];

  fields?.forEach((field) => {
    const currentField = loc.attributes[`${field}`];
    if (currentField) {
      // "notnull" example: childcare has age groups with a max age field; if max age is not null then the location has that age group
      // "byvalue" example: food help has "food panty type" and "soup kitchen type" fields where the value of the fields is the service e.g. "soup kitchen - kosher"
      if (format === "byvalue") {
        services.push(currentField);
      } else if (format === "notnull") {
        services.push(fieldLabels[field]);
      }
    }
  });

  return services.length === 0 ? undefined : services;
};

export const getLocCardDetails = ({
  loc,
  locationLayerInfo,
  locationLayer,
  hideDistance,
  t,
  fieldLabels,
  language,
}: {
  loc: Graphic;
  locationLayerInfo: LocationsLayerInfo;
  locationLayer?: FeatureLayer;
  hideDistance?: boolean;
  t: TFunction;
  fieldLabels: any;
  language: string;
}) => {
  if (locationLayer && locationLayerInfo.domainFieldNames) {
    updateDomainFields([loc], locationLayer, locationLayerInfo.domainFieldNames);
  }

  const locAttr = loc?.attributes || {};
  const phone = locationLayerInfo.phoneFieldName ? locAttr[locationLayerInfo.phoneFieldName] : "";
  const website = locationLayerInfo.websiteFieldName
    ? locAttr[locationLayerInfo.websiteFieldName]
    : "";
  const address = locationLayerInfo.addressFieldName
    ? locAttr[locationLayerInfo.addressFieldName]
    : "";
  const address2 = locationLayerInfo.address2FieldName
    ? locAttr[locationLayerInfo.address2FieldName]
    : "";
  const objectId = locAttr[locationLayerInfo.objectIdFieldName];

  const locMessage = locationLayerInfo.locMessageFieldNames
    ?.map((fieldName) => locAttr[fieldName])
    .filter((note) => !!note)
    .join(" - ");

  const locEntranceInformation = locationLayerInfo.entranceInformation
    ? locAttr[locationLayerInfo.entranceInformation]
    : "";

  // ListHelper updates isOpenNow for location objects in the List because it's slightly more efficient to do en masse
  // but Cards that will show in map popup need to have the location updated here
  if (
    locationLayerInfo.openHoursServiceTypes &&
    locationLayerInfo.openHoursTimeWindows &&
    locAttr.isOpenNow !== true &&
    locAttr.isOpenNow !== false
  ) {
    updateOpenNow(
      [loc],
      locationLayerInfo.openHoursServiceTypes,
      locationLayerInfo.openHoursTimeWindows,
      t
    );
  }
  const isOpen = loc?.attributes?.isOpenNow;
  let hoursNote = loc?.attributes?.nextCloseTime
    ? t("locCard.closesAt", { closeTime: loc.attributes.nextCloseTime })
    : undefined;
  if (!hoursNote && loc?.attributes?.nextOpenTime) {
    // if Chinese, put day before time
    if (language === "zh-CN") {
      const nextOpenTimeAry = loc?.attributes?.nextOpenTime.split(" ");
      const day = nextOpenTimeAry.pop();
      nextOpenTimeAry.splice(0, 0, day);
      const reformattedOpenTime = nextOpenTimeAry.join(" ");
      hoursNote = t("locCard.opensAt", { openTime: reformattedOpenTime });
    } else {
      hoursNote = t("locCard.opensAt", { openTime: loc?.attributes?.nextOpenTime });
    }
  }

  // updates for right-to-left languages
  if (language === "ar" || language === "ur" || language === "yi") {
    const splitItems = hoursNote?.split(" ");

    if (!splitItems || splitItems.length < 3) {
      // console.warn(hoursNote + " is not in an expected format")
    } else {
      // swap the first 2 items (this step is always needed to swap the time and the AM/PM)
      const tempItem = JSON.stringify(splitItems[0]);
      splitItems[0] = JSON.parse(JSON.stringify(splitItems[1]));
      splitItems[1] = JSON.parse(tempItem);

      if (
        (splitItems?.length === 4 && language === "ar") ||
        (splitItems?.length === 5 && language === "yi") ||
        (splitItems?.length === 4 && language === "ur" && splitItems[3] !== "ہوگا")
      ) {
        // move the day of the week to be before (programming terms) the time (or last (in language terms) if you read it right to left)
        // Remove the 3rd item (index 2) and store it
        const item = splitItems.splice(2, 1)[0];
        // Add it to the beginning of the array
        splitItems.unshift(item);
      }
    }
    hoursNote = splitItems?.join(" ");
  }

  const locIsAccessible =
    locationLayerInfo.accessibleFieldName && locationLayerInfo.accessibleFieldValue
      ? loc?.attributes[locationLayerInfo.accessibleFieldName] ===
        locationLayerInfo.accessibleFieldValue
      : false;

  const locIsPetFriendly =
    locationLayerInfo.petFriendlyFieldName && locationLayerInfo.petFriendlyFieldValue
      ? loc?.attributes[locationLayerInfo.petFriendlyFieldName] ===
        locationLayerInfo.petFriendlyFieldValue
      : false;

  const locAllAges =
    locationLayerInfo.allAgesFieldName && locationLayerInfo.allAgesFieldValue
      ? loc?.attributes[locationLayerInfo.allAgesFieldName] === locationLayerInfo.allAgesFieldValue
      : false;

  const locAcceptsVouchers =
    locationLayerInfo.acceptsVouchersFieldName && locationLayerInfo.acceptsVouchersFieldValue !== undefined && locationLayerInfo.acceptsVouchersFieldValue !== ""
      ? String(loc?.attributes[locationLayerInfo.acceptsVouchersFieldName]) ===
        String(locationLayerInfo.acceptsVouchersFieldValue)
      : false;

  const cardDetails = {
    locTitle: locAttr[locationLayerInfo.titleFieldName],
    locSubtitle: locationLayerInfo.subtitleFieldName
      ? locationLayerInfo.subtitleFieldName.includes(";")
        ? locationLayerInfo.subtitleFieldName
            .split(";")
            .map((fieldName) => locAttr[fieldName])
            .join(" - ")
        : locAttr[locationLayerInfo.subtitleFieldName]
      : "",
    locType: locationLayerInfo.locTypeFieldName
      ? locAttr[locationLayerInfo.locTypeFieldName]
      : undefined,
    locDist:
      !hideDistance && locAttr.proximity
        ? t("locCard.milesAbbrev", { proximity: locAttr.proximity })
        : undefined,
    locAddress: address,
    locAddress2: address2,
    locStatus: { isOpen, hoursNote },
    locPhone: phone,
    locWebsite: website,
    locServices: _getServices(
      loc,
      locationLayerInfo.servicesFormat,
      fieldLabels,
      locationLayerInfo.serviceTypeFieldNames
    ),
    locMessage: locMessage,
    locFID: objectId,
    locIsAccessible,
    locIsPetFriendly,
    locAllAges,
    locAcceptsVouchers,
    locEntranceInformation,
    CARETYPE_NAME: loc.attributes?.["CARETYPE_NAME"],
  };
  return cardDetails;
};
